import React, { useEffect, useMemo, useState } from "react";
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, format, isSameMonth } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { todayLocal } from "../../lib/utils";
import { subscriptionCalendarDayMeta } from "../../lib/statusMaps";
import { apiClient } from "../../lib/apiClient";
import { DeliveryDetailDialog } from "./DeliveryDetailDialog";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function LegendDot({ dotClass, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
}

// Month-grid view of a subscription's deliveries, color-coded Delivered /
// Not Delivered / Pending (due, undelivered) / Scheduled (future) / Paused
// (skipped or on hold) / Off (outlet closed that weekday) / Unselected (this
// subscriber didn't pick that weekday). Reused wherever an admin needs an
// at-a-glance calendar for one subscription's deliveries (Subscription
// Detail's Delivery History tab).
export function DeliveryCalendar({ deliveries, subscription, skippedDates }) {
  const [month, setMonth] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [outletDays, setOutletDays] = useState(null);
  const today = todayLocal();

  useEffect(() => {
    apiClient.get("/outlet/deliver-days").then(({ data }) => setOutletDays(data)).catch(() => {});
  }, []);

  const byDate = useMemo(() => {
    const m = new Map();
    deliveries.forEach((d) => { if (d.date) m.set(d.date, d); });
    return m;
  }, [deliveries]);

  const skippedSet = useMemo(() => new Set(skippedDates || []), [skippedDates]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month));
    const end = endOfWeek(endOfMonth(month));
    const out = [];
    let d = start;
    while (d <= end) { out.push(d); d = addDays(d, 1); }
    return out;
  }, [month]);

  return (
    <div data-testid="delivery-calendar">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex overflow-hidden rounded-md border border-primary bg-primary text-white">
            <button type="button" onClick={() => setMonth((m) => subMonths(m, 1))} className="px-3 py-1.5 hover:bg-primary-hover" data-testid="delivery-calendar-prev">
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
            <div className="w-px bg-white/25" />
            <button type="button" onClick={() => setMonth((m) => addMonths(m, 1))} className="px-3 py-1.5 hover:bg-primary-hover" data-testid="delivery-calendar-next">
              <ChevronRight size={16} strokeWidth={2} />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setMonth(new Date())}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-text-main hover:bg-border/40"
            data-testid="delivery-calendar-today-button"
          >
            Today
          </button>
        </div>
        <p className="font-heading text-xl font-semibold text-text-main sm:text-2xl">{format(month, "MMMM yyyy")}</p>
      </div>

      <div className="overflow-hidden rounded-md border border-border">
        <div className="grid grid-cols-7 border-b border-border bg-surface">
          {WEEKDAYS.map((wd, i) => (
            <div key={wd} className={`py-2 text-center text-sm font-semibold text-primary ${i !== 6 ? "border-r border-border" : ""}`}>
              {wd}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const key = format(d, "yyyy-MM-dd");
            const delivery = byDate.get(key);
            const inMonth = isSameMonth(d, month);
            const isToday = key === today;
            const meta = subscriptionCalendarDayMeta({
              dateStr: key, weekday: WEEKDAY_KEYS[d.getDay()], delivery, todayStr: today,
              deliverDays: subscription?.deliver_days, outletDays, skippedDates: skippedSet,
              startDate: subscription?.start_date, endDate: subscription?.end_date,
            });
            const notLastCol = (i + 1) % 7 !== 0;
            return (
              <button
                key={key}
                type="button"
                disabled={!meta}
                onClick={() => setSelected({ date: key, delivery, meta })}
                className={`relative min-h-[92px] border-b border-border p-2 text-left align-top sm:min-h-[104px] ${notLastCol ? "border-r" : ""} ${
                  !inMonth ? "bg-background/60" : meta ? meta.bg : ""
                } ${isToday ? "ring-2 ring-inset ring-accent" : ""} ${meta ? "cursor-pointer hover:brightness-95" : "cursor-default"}`}
                data-testid={`delivery-calendar-day-${key}`}
              >
                {meta && <span className={`block text-xs font-medium ${meta.text}`}>{meta.label}</span>}
                <span className={`absolute right-2 top-2 text-sm font-semibold ${inMonth ? "text-primary" : "text-text-muted/30"}`}>
                  {format(d, "d")}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-text-muted" data-testid="delivery-calendar-legend">
        <LegendDot dotClass="bg-green-500" label="Delivered" />
        <LegendDot dotClass="bg-red-500" label="Not Delivered" />
        <LegendDot dotClass="bg-amber-500" label="Pending" />
        <LegendDot dotClass="bg-blue-500" label="Scheduled" />
        <LegendDot dotClass="bg-purple-500" label="Paused" />
        <LegendDot dotClass="bg-slate-500" label="Off" />
        <LegendDot dotClass="bg-stone-400" label="Unselected" />
      </div>

      {selected && (
        <DeliveryDetailDialog
          open={!!selected}
          onOpenChange={(v) => !v && setSelected(null)}
          delivery={selected.delivery}
          date={selected.date}
          statusLabel={selected.meta?.label}
        />
      )}
    </div>
  );
}
